import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate, useSearch } from '@tanstack/react-router'
import { VendorFilters } from '@/pages/vendors/VendorFilters'
import { VendorList } from '@/pages/vendors/VendorList'
import { VendorListFilterSchema } from '@deepguide-ai/types'
import { Input } from '@deepguide-ai/ui/input'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import { DEFAULT_PAGE_SIZE, paginationSearchSchema } from '@/lib/pagination'
import { trpc } from '@/lib/trpc'
import { useHipaaEnabled } from '@/hooks/useComplianceFramework'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'

export const auditorVendorSearchSchema = VendorListFilterSchema.pick({
  search: true,
  riskTier: true,
  status: true,
}).extend(paginationSearchSchema.shape)

export function AuditorVendorsPage() {
  const { workspaceId, auditCycleId } = useParams({ strict: false })
  const filters = auditorVendorSearchSchema.parse(useSearch({ strict: false }))
  const navigate = useNavigate()
  const hipaaEnabled = useHipaaEnabled()
  const page = filters.page ?? 1
  const pageSize = filters.pageSize ?? DEFAULT_PAGE_SIZE
  const vendors = useQuery(
    trpc.auditor.getVendors.queryOptions({
      ...filters,
      auditCycleId: auditCycleId!,
      page,
      pageSize,
    })
  )
  const update = (next: Partial<typeof filters>) =>
    navigate({
      to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/vendors',
      params: { workspaceId: workspaceId!, auditCycleId: auditCycleId! },
      search: { ...filters, page: 1, ...next },
      replace: true,
    })
  return (
    <div className='space-y-4 p-6'>
      <ScreenataPageHeader
        title='Vendors'
        titleClassName='text-foreground'
        description='Current program vendors, including offboarded vendors. Read-only.'
      />
      <div className='flex flex-wrap items-center gap-3'>
        <VendorFilters
          riskTier={filters.riskTier ?? 'all'}
          status={filters.status ?? 'all'}
          includeOffboarded
          onRiskTierChange={(value) =>
            void update({ riskTier: value === 'all' ? undefined : value })
          }
          onStatusChange={(value) =>
            void update({ status: value === 'all' ? undefined : value })
          }
        />
        <Input
          aria-label='Search vendors'
          placeholder='Search name or service…'
          value={filters.search ?? ''}
          onChange={(event) =>
            void update({ search: event.target.value || undefined })
          }
          className='w-full sm:ml-auto sm:max-w-xs'
        />
      </div>
      {vendors.isPending && (
        <TableSkeleton
          columns={hipaaEnabled ? 12 : 11}
          rows={5}
          className='bg-card rounded-md'
          cellClassName='px-3 py-2.5'
        />
      )}
      {vendors.isError && <p role='alert'>{vendors.error.message}</p>}
      {vendors.data &&
        (vendors.data.items.length === 0 ? (
          <TableEmptyState
            message='No vendors match these filters.'
            className='bg-card rounded-md'
          />
        ) : (
          <VendorList
            items={vendors.data.items}
            hipaaEnabled={hipaaEnabled}
            page={page}
            pageSize={pageSize}
            totalItems={vendors.data.total}
            onPageChange={(page) => void update({ page })}
            onPageSizeChange={(pageSize) => void update({ pageSize })}
            onOpenVendor={(vendorId) =>
              void navigate({
                to: '/workspaces/$workspaceId/auditor-center/$auditCycleId/vendors/$vendorId',
                params: {
                  workspaceId: workspaceId!,
                  auditCycleId: auditCycleId!,
                  vendorId,
                },
                search: { ...filters, tab: 'overview' },
              })
            }
          />
        ))}
      <p className='text-text-subtle text-[11px] leading-normal'>
        Current register, not an audit-period snapshot.
      </p>
    </div>
  )
}
